'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import NProgress from 'nprogress';
import { useEffect, useRef } from 'react';

import 'nprogress/nprogress.css';

NProgress.configure({
  showSpinner: false,
  minimum: 0.08,
  trickleSpeed: 120,
});

const PROGRESS_COLOR = '#ffe959';

const ensureStyles = () => {
  const id = '__nprogress_fallback_css__';

  if (document.getElementById(id)) return;

  const style = document.createElement('style');

  style.id = id;
  style.textContent = `
    #nprogress {
      pointer-events: none;
    }

    #nprogress .bar {
      background: ${PROGRESS_COLOR};
      position: fixed;
      z-index: 999999;
      top: 0;
      left: 0;
      width: 100%;
      height: 2px;
    }

    #nprogress .peg {
      box-shadow: 0 0 10px ${PROGRESS_COLOR}, 0 0 5px ${PROGRESS_COLOR};
    }
  `;

  document.head.appendChild(style);
};

type RouterProgressProps = {
  delay?: number;
};

export default function RouterProgress({ delay = 80 }: RouterProgressProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const startedRef = useRef(false);
  const navigatingRef = useRef(false);

  const delayTimerRef = useRef<number | null>(null);
  const hardTimerRef = useRef<number | null>(null);
  const popstateTimerRef = useRef<number | null>(null);

  const HARD_TIMEOUT_MS = 4000;
  const POPSTATE_TIMEOUT_MS = 700;

  const clearTimer = (ref: React.MutableRefObject<number | null>) => {
    if (ref.current) {
      window.clearTimeout(ref.current);
      ref.current = null;
    }
  };

  const clearAllTimers = () => {
    clearTimer(delayTimerRef);
    clearTimer(hardTimerRef);
    clearTimer(popstateTimerRef);
  };

  const finish = () => {
    clearAllTimers();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        NProgress.done(true);
        startedRef.current = false;
        navigatingRef.current = false;
      });
    });
  };

  const start = () => {
    if (startedRef.current) return;

    startedRef.current = true;
    navigatingRef.current = true;

    clearAllTimers();

    delayTimerRef.current = window.setTimeout(() => {
      ensureStyles();
      NProgress.start();

      hardTimerRef.current = window.setTimeout(() => {
        finish();
      }, HARD_TIMEOUT_MS);
    }, delay);
  };

  // отдельная логика для стрелок браузера
  const startPopstate = () => {
    start();

    popstateTimerRef.current = window.setTimeout(() => {
      finish();
    }, POPSTATE_TIMEOUT_MS);
  };

  useEffect(() => {
    NProgress.remove();

    return () => {
      clearAllTimers();
      NProgress.remove();
    };
  }, []);

  useEffect(() => {
    const onClickCapture = (e: MouseEvent) => {
      try {
        if (e.defaultPrevented || e.button !== 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

        let el = e.target as HTMLElement | null;

        while (el && el.nodeName !== 'A') {
          el = el.parentElement;
        }

        const link = el as HTMLAnchorElement | null;

        if (!link) return;
        // Включать только для помеченных ссылок
        if (!link.hasAttribute('data-rp')) return;
        if (link.target === '_blank') return;
        if (link.hasAttribute('download')) return;

        const href = link.getAttribute('href');

        if (!href || href === '#' || href.startsWith('javascript:')) return;

        const url = new URL(link.href, window.location.href);

        if (url.origin !== window.location.origin) return;

        const currentUrl = window.location.pathname + window.location.search;
        const nextUrl = url.pathname + url.search;

        if (nextUrl === currentUrl) return;

        start();
      } catch (error) {
        // никогда не допускаем «пузыря» ошибки вверх
        console.error('[RouterProgress click]', error);
        finish();
      }
    };

    const onPopState = () => {
      startPopstate();
    };

    const onPageShow = () => {
      finish();
    };

    const onError = () => {
      if (startedRef.current) {
        finish();
      }
    };

    document.addEventListener('click', onClickCapture, true);
    window.addEventListener('popstate', onPopState);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onError);

    return () => {
      document.removeEventListener('click', onClickCapture, true);
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onError);
    };
  }, [delay]);

  useEffect(() => {
    if (!navigatingRef.current) return;

    finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams?.toString()]);

  return null;
}
